import React, { useState, useEffect, createContext, useContext } from 'react';
import {
  Box,
  createTheme,
  ThemeProvider,
  useMediaQuery
} from '@mui/material';

// Theme Context
interface ThemeContextType {
  isDarkMode: boolean;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useThemeContext = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useThemeContext must be used within a ThemeManager');
  }
  return context;
};

export const lightTheme = createTheme({
  typography: {
    fontFamily: '"Nunito Sans", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontFamily: '"Nunito Sans", sans-serif',
      fontWeight: 800,
    },
    h2: {
      fontFamily: '"Nunito Sans", sans-serif',
      fontWeight: 700,
    },
    h3: {
      fontFamily: '"Nunito Sans", sans-serif',
      fontWeight: 600,
    },
    h4: {
      fontFamily: '"Nunito Sans", sans-serif',
      fontWeight: 600,
    },
    h5: {
      fontFamily: '"Nunito Sans", sans-serif',
      fontWeight: 600,
    },
    h6: {
      fontFamily: '"Nunito Sans", sans-serif',
      fontWeight: 600,
    },
    subtitle1: {
      fontFamily: '"Nunito Sans", sans-serif',
      fontWeight: 500,
    },
    subtitle2: {
      fontFamily: '"Nunito Sans", sans-serif',
      fontWeight: 500,
    },
    body1: {
      fontFamily: '"Nunito Sans", sans-serif',
      fontWeight: 400,
    },
    body2: {
      fontFamily: '"Nunito Sans", sans-serif',
      fontWeight: 400,
    },
    button: {
      fontFamily: '"Nunito Sans", sans-serif',
      fontWeight: 600,
      textTransform: 'none',
    },
    caption: {
      fontFamily: '"Nunito Sans", sans-serif',
      fontWeight: 400,
    },
    overline: {
      fontFamily: '"Nunito Sans", sans-serif',
      fontWeight: 500,
    },
  },
  palette: {
    mode: 'light',
    primary: {
      main: '#0B74D1',
      dark: '#005a9e',
      light: '#338eda',
    },
    secondary: {
      main: '#3f51b5',
      dark: '#002984',
      light: '#6573c3',
    },
    text: {
      primary: '#1b1b1b',
      secondary: '#333333',
    },
    background: {
      paper: '#ffffff',
      default: '#f4f8ffff',
    },
    success: {
      main: '#6bbe6eff',
      dark: '#00701a',
      light: '#76d275',
    },
    info: {
      main: '#f57c00',
      dark: '#bb4d00',
      light: '#ffad42',
    },
    error: {
      main: '#f44336',
      dark: '#d32f2f',
      light: '#EF5350',
    },
    warning: {
      main: '#f4511e',
      dark: '#b91400',
      light: '#ff784e',
    },
    divider: 'rgba(0, 0, 0, 0.12)',
    action: {
      active: 'rgba(0, 0, 0, 0.54)',
      hover: 'rgba(0, 0, 0, 0.04)',
      selected: 'rgba(0, 0, 0, 0.08)',
      disabled: 'rgba(0, 0, 0, 0.26)',
      disabledBackground: 'rgba(0, 0, 0, 0.12)',
    },
  },
  components: {
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
      },
    },
  },
});

export const darkTheme = createTheme({
  typography: {
    fontFamily: '"Nunito Sans", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontFamily: '"Nunito Sans", sans-serif',
      fontWeight: 800,
    },
    h2: {
      fontFamily: '"Nunito Sans", sans-serif',
      fontWeight: 700,
    },
    h3: {
      fontFamily: '"Nunito Sans", sans-serif',
      fontWeight: 600,
    },
    h4: {
      fontFamily: '"Nunito Sans", sans-serif',
      fontWeight: 600,
    },
    h5: {
      fontFamily: '"Nunito Sans", sans-serif',
      fontWeight: 600,
    },
    h6: {
      fontFamily: '"Nunito Sans", sans-serif',
      fontWeight: 600,
    },
    subtitle1: {
      fontFamily: '"Nunito Sans", sans-serif',
      fontWeight: 500,
    },
    subtitle2: {
      fontFamily: '"Nunito Sans", sans-serif',
      fontWeight: 500,
    },
    body1: {
      fontFamily: '"Nunito Sans", sans-serif',
      fontWeight: 400,
    },
    body2: {
      fontFamily: '"Nunito Sans", sans-serif',
      fontWeight: 400,
    },
    button: {
      fontFamily: '"Nunito Sans", sans-serif',
      fontWeight: 600,
      textTransform: 'none',
    },
    caption: {
      fontFamily: '"Nunito Sans", sans-serif',
      fontWeight: 400,
    },
    overline: {
      fontFamily: '"Nunito Sans", sans-serif',
      fontWeight: 500,
    },
  },
  palette: {
    mode: 'dark',
    primary: {
      main: '#0B74D1',
      dark: '#005a9e',
      light: '#338eda',
    },
    secondary: {
      main: '#7c4dff',
      dark: '#651fff',
      light: '#b388ff',
    },
    text: {
      primary: '#ffffff',
      secondary: 'rgba(255, 255, 255, 0.7)',
    },
    background: {
      paper: '#1e1e1e',
      default: '#121212',
    },
    success: {
      main: '#66bb6a',
      dark: '#2e7d32',
      light: '#a5d6a7',
    },
    info: {
      main: '#f57c00',
      dark: '#bb4d00',
      light: '#ffad42',
    },
    error: {
      main: '#f44336',
      dark: '#d32f2f',
      light: '#e46767ff',
    },
    warning: {
      main: '#ff9800',
      dark: '#f57c00',
      light: '#ffb74d',
    },
    divider: 'rgba(255, 255, 255, 0.12)',
    action: {
      active: 'rgba(255, 255, 255, 0.56)',
      hover: 'rgba(255, 255, 255, 0.08)',
      selected: 'rgba(255, 255, 255, 0.12)',
      disabled: 'rgba(255, 255, 255, 0.3)',
      disabledBackground: 'rgba(255, 255, 255, 0.12)',
    },
    // Add custom palette entries for dashboard-specific colors
    grey: {
      50: '#2a2a2a',
      100: '#3a3a3a',
      200: '#4a4a4a',
      300: '#5a5a5a',
      400: '#6a6a6a',
      500: '#7a7a7a',
    },
  },
  components: {
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          backgroundColor: '#1e1e1e',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: '#1e1e1e',
          borderBottom: '1px solid rgba(255, 255, 255, 0.12)',
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: '#1e1e1e',
          borderRight: '1px solid rgba(255, 255, 255, 0.12)',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundColor: '#1e1e1e',
          borderColor: 'rgba(255, 255, 255, 0.12)',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: '1px solid rgba(255, 255, 255, 0.12)',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            '& fieldset': {
              borderColor: 'rgba(255, 255, 255, 0.23)',
            },
            '&:hover fieldset': {
              borderColor: 'rgba(255, 255, 255, 0.4)',
            },
            '&.Mui-focused fieldset': {
              borderColor: '#4dabf7',
            },
          },
        },
      },
    },
  },
});

interface ThemeManagerProps {
  children?: React.ReactNode;
}

const ThemeManager: React.FC<ThemeManagerProps> = ({ children }) => {
  // Check for system preference
  const prefersDarkMode = useMediaQuery('(prefers-color-scheme: dark)');
  
  // Initialize theme state from localStorage or system preference
  const [isDarkMode, setIsDarkMode] = useState(false);

  // Update localStorage when theme changes
  useEffect(() => {
    localStorage.setItem('themeMode', isDarkMode ? 'dark' : 'light');
  }, [isDarkMode]);

  // Update theme when system preference changes (if no manual selection was made)
  useEffect(() => {
    const savedTheme = localStorage.getItem('themeMode');
    if (savedTheme === null) {
      setIsDarkMode(prefersDarkMode);
    }
  }, [prefersDarkMode]);

  const toggleTheme = () => {
    setIsDarkMode((prev) => !prev);
  };

  const currentTheme = isDarkMode ? darkTheme : lightTheme;

  const contextValue: ThemeContextType = {
    isDarkMode,
    toggleTheme,
  };

  return (
    <ThemeContext.Provider value={contextValue}>
      <ThemeProvider theme={currentTheme}>
        <Box
          sx={{
            bgcolor: 'background.default',
            minHeight: '100vh',
            transition: 'background-color 0.3s ease, color 0.3s ease',
          }}
        >
          {children}
        </Box>
      </ThemeProvider>
    </ThemeContext.Provider>
  );
};

export default ThemeManager;